import dayjs, { Dayjs } from "dayjs";
import { FC, ReactNode } from "react";
import { CSSProp } from "styled-components";
import tw from "twin.macro";

import CheckIcon from "../../assets/icons/check.svg";

import { Avatar } from "./Avatar";

const balloonLeft = tw`bg-gradient-theme-to-l rounded-bl-none text-white`;

const Position = {
  left: "left",
  right: "right",
} as const;

export interface Props {
  position?: keyof typeof Position;
  icon?: string;
  fallbackIcon?: string;
  renderIcon?: ReactNode;
  check?: boolean;
  date?: string | Dayjs;
  classes?: {
    root?: CSSProp;
    balloon?: CSSProp;
  };
  className?: string;
}

/**
 * @param children ReactNode
 * @param position "left" | "right"(default: "right")
 * @param icon string
 * @param fallbackIcon string
 * @param renderIcon ReactNode
 * @param check boolean(default: false)
 * @param date Dayjs
 * @param classes { root: CSSProps, balloon: CSSProps }
 * @param className string
 */
export const Balloon: FC<Props> = ({
  children,
  position = "right",
  icon,
  fallbackIcon,
  renderIcon,
  check = false,
  date,
  classes: propsClasses,
  className,
}) => {
  const isAlignLeft = position === Position.left;

  return (
    <div
      tw="flex items-end self-end max-w-[90%]"
      css={[isAlignLeft && tw`self-start`, propsClasses?.root]}
      className={className}
    >
      {!renderIcon && icon && isAlignLeft && (
        <Avatar tw="mb-4 mr-2" src={icon} fallback={fallbackIcon} />
      )}
      {renderIcon && <div tw="mb-4 mr-2">{renderIcon}</div>}
      <div
        tw="flex flex-col items-end w-full"
        css={isAlignLeft && tw`items-start`}
      >
        <div
          tw="bg-gray p-3 rounded-[20px] max-w-[100%]"
          css={[
            isAlignLeft ? balloonLeft : tw`rounded-br-none`,
            propsClasses?.balloon,
          ]}
        >
          <span tw="text-base leading-6 whitespace-pre-wrap break-all">
            {children}
          </span>
        </div>
        <div tw="flex items-center mt-2">
          {date && (
            <div tw="text-2xs text-secondary">
              {dayjs(date).format("YYYY/MM/DD HH:mm")}
            </div>
          )}
          {check && <CheckIcon tw="svg ml-1 text-theme" />}
        </div>
      </div>
      {icon && !isAlignLeft && <Avatar tw="mb-4 ml-2" src={icon} />}
    </div>
  );
};
